import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SidebarProvider } from "./contexts/SidebarContext";
import { AuthProvider } from "./contexts/AuthContext";       // ← your context
import ProtectedRoute from "./components/ProtectedRoute";
import { ROUTE_ACCESS } from "./lib/accessControlRegistry";

// ── public pages ─────────────────────────────────────────────────────────────
import Login from "./pages/login";
import ForgotPassword from "./pages/forgot-password";
import Unauthorized from "./pages/Unauthorized";

// ── shared (any logged-in user) ──────────────────────────────────────────────
import Dashboard from "./pages/dashboard";
import TrainingVideosLibrary from "./pages/training-videos-library";

import RappelDePaiement from './pages/billing/RappelDePaiement';


// ── admin ────────────────────────────────────────────────────────────────────
import AdminUsersManagement from "./pages/admin-users-management";
import MultiRoleUserProfiles from "./pages/multi-role-user-profiles";
import BillingOverview from "./pages/billing-overview";
import AdvancedBillingManagement from "./pages/advanced-billing-management";
import AdvancedPaymentConfiguration from "./pages/advanced-payment-configuration";
import PaymentReminderSystem from "./pages/payment-reminder-system";
import CommercialPackagesManagement from "./pages/commercial-packages-management";
import InternationalMarketManagement from "./pages/international-market-management";
import ServicesManagement from "./pages/services-management";
import CountryServiceConfiguration from "./pages/country-service-configuration";
import AdminPacksServicesPage from "./pages/admin-packs-services";
import AdminIntegrationsPage from "./pages/admin-integrations";
import AdminPlatformMessagesPage from "./pages/admin-platform-messages";
import ServicePackageIntegration from "./pages/service-package-integration";
import SupplierManagementDashboard from "./pages/supplier-management-dashboard";
import MarketSupplierConfiguration from "./pages/market-supplier-configuration";
import PhaseTemplatesPage from './pages/phase-templates/PhaseTemplatesPage';
import NotificationsPage from './pages/Notifications';



// ── dentist / clinic ─────────────────────────────────────────────────────────
import PatientProfile from "./pages/patient-profile";
import CasesList from "./pages/cases-list";
import CaseDetail from "./pages/case-detail";
import PatientsListPage from "./pages/patients-list";
import PackUser from "./pages/pack-users";
import IPRStrippingSheet from './pages/ipr-stripping-sheet';
import ProductionSheetPage from './pages/production-sheet';
import DentistHubPage from './pages/dentist-hub';
import CaseDentistAssistantPage from './pages/case-dentist-assistant/CaseDentistAssistantPage';
import DentistBillingPackPage from './pages/dentist-billing-pack/DentistBillingPackPage';

// ── lab / production ─────────────────────────────────────────────────────────
import ProductionTrackingDashboard from "./pages/production-tracking-dashboard";
import MachineFleetManagement from "./pages/machine-fleet-management";
import ProductionSupplierInterface from "./pages/production-supplier-interface";
import QRCodeProductionTracking from "./pages/qr-code-production-tracking";
import ManufacturerProductionDashboard from "./pages/manufacturer-production-dashboard";
import WorkshopLotPage from "./pages/workshop-lot";
import BillingPhasesManagement from './pages/BillingPhasesManagement/BillingPhasesManagement';
import DentistInvoicesTracking from './pages/DentistInvoicesTracking/DentistInvoicesTracking';
import ChangePasswordScreen from './pages/auth/ChangePasswordScreen';
import ModuleNotAvailable from './pages/ModuleNotAvailable';

// ── client / delivery ────────────────────────────────────────────────────────
import ClientDeliveryTrackingPortal from "./pages/client-delivery-tracking-portal";

// Accès routes : src/lib/accessControlRegistry.js (ROUTE_ACCESS)

const Routes = () => (
  <BrowserRouter>
    <ErrorBoundary>
      <ThemeProvider>
        {/* AuthProvider wraps everything so useAuth() works in ProtectedRoute */}
        <AuthProvider>
          <SidebarProvider>
          <ScrollToTop />
          <RouterRoutes>

            {/* ── PUBLIC (no auth needed) ──────────────────────────────── */}
            <Route path="/"              element={<Login />} />
            <Route path="/login"         element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/unauthorized"  element={<Unauthorized />} />

          
            {/* ── SHARED – any authenticated user ──────────────────────── */}
            <Route element={<ProtectedRoute allowedRoles={ROUTE_ACCESS.sharedHome.allowedRoles} allowedPermissions={ROUTE_ACCESS.sharedHome.allowedPermissions} />}>
              <Route path="/dashboard"              element={<Dashboard />} />
              <Route path="/change-password" element={<ChangePasswordScreen />} />
              <Route path="/notifications" element={<NotificationsPage />} />
            </Route>

            <Route element={<ProtectedRoute allowedRoles={ROUTE_ACCESS.training.allowedRoles} allowedPermissions={ROUTE_ACCESS.training.allowedPermissions} />}>
              <Route path="/training-videos-library" element={<TrainingVideosLibrary />} />
            </Route>
      
      
       <Route element={<ProtectedRoute allowedRoles={ROUTE_ACCESS.packUser.allowedRoles} allowedPermissions={ROUTE_ACCESS.packUser.allowedPermissions} />}>
                           <Route path="/pack-user" element={<PackUser />} />
            </Route>

            <Route element={<ProtectedRoute allowedRoles={ROUTE_ACCESS.dentistHub.allowedRoles} allowedPermissions={ROUTE_ACCESS.dentistHub.allowedPermissions} />}>
              <Route path="/dentiste" element={<DentistHubPage />} />
              <Route path="/case/:caseId/assistant" element={<CaseDentistAssistantPage />} />
              <Route path="/dentiste/facturation-pack" element={<DentistBillingPackPage />} />
            </Route>


            {/* ── ADMIN ────────────────────────────────────────────────── */}
            <Route element={<ProtectedRoute allowedRoles={ROUTE_ACCESS.admin.allowedRoles} allowedPermissions={ROUTE_ACCESS.admin.allowedPermissions} />}>
            <Route path="/ipr-stripping-sheet" element={<IPRStrippingSheet />} />
              <Route path="/admin-users-management"          element={<AdminUsersManagement />} />
              <Route path="/multi-role-user-profiles"        element={<MultiRoleUserProfiles />} />
              <Route path="/billing-overview"                element={<BillingOverview />} />
              <Route path="/advanced-billing-management"     element={<AdvancedBillingManagement />} />
              <Route path="/advanced-payment-configuration"  element={<AdvancedPaymentConfiguration />} />
              <Route path="/payment-reminder-system"         element={<PaymentReminderSystem />} />
              <Route path="/admin/packs-services" element={<AdminPacksServicesPage />} />
              <Route path="/admin/integrations" element={<AdminIntegrationsPage />} />
              <Route path="/admin/platform-messages" element={<AdminPlatformMessagesPage />} />
              <Route path="/commercial-packages-management" element={<CommercialPackagesManagement />} />
              <Route path="/international-market-management" element={<InternationalMarketManagement />} />
              <Route path="/services-management" element={<ServicesManagement />} />
              <Route path="/country-service-configuration" element={<CountryServiceConfiguration />} />
              <Route path="/service-package-integration"     element={<ServicePackageIntegration />} />
              <Route path="/supplier-management-dashboard"   element={<SupplierManagementDashboard />} />
              <Route path="/market-supplier-configuration"   element={<MarketSupplierConfiguration />} />
                <Route path="/phase-templates"                 element={<PhaseTemplatesPage />} />  {/* ← ADD HERE */}
<Route path="/billing-phases-management" element={<BillingPhasesManagement />} />
      <Route path="/rappel-paiement" element={<RappelDePaiement />} />

<Route 
  path="/dentist-invoices-tracking" 
  element={<DentistInvoicesTracking />} 
/>
              <Route path="/stock-management" element={<ModuleNotAvailable />} />

            </Route>

            {/* ── Liste patients : admin (+ dentistes avec leurs dossiers uniquement côté page) ─ */}
            <Route element={<ProtectedRoute allowedRoles={ROUTE_ACCESS.patients.allowedRoles} allowedPermissions={ROUTE_ACCESS.patients.allowedPermissions} />}>
              <Route path="/patients-list" element={<PatientsListPage />} />
            </Route>
            <Route element={<ProtectedRoute allowedRoles={ROUTE_ACCESS.patientProfile.allowedRoles} allowedPermissions={ROUTE_ACCESS.patientProfile.allowedPermissions} />}>
              <Route path="/patient-profile/:patientId" element={<PatientProfile />} />
            </Route>
            <Route element={<ProtectedRoute allowedRoles={ROUTE_ACCESS.cases.allowedRoles} allowedPermissions={ROUTE_ACCESS.cases.allowedPermissions} />}>
           
              <Route path="/cases-list"       element={<CasesList />} />
              <Route path="/case-detail/:caseId"      element={<CaseDetail />} />
            </Route>

            {/* ── LAB / PRODUCTION  (admin can also access) ───────────── */}
            <Route element={<ProtectedRoute allowedRoles={ROUTE_ACCESS.productionDesk.allowedRoles} allowedPermissions={ROUTE_ACCESS.productionDesk.allowedPermissions} />}>
              <Route path="/production-tracking-dashboard"      element={<ProductionTrackingDashboard />} />
              <Route path="/machine-fleet-management"           element={<MachineFleetManagement />} />
              <Route path="/production-supplier-interface"      element={<ProductionSupplierInterface />} />
              <Route path="/qr-code-production-tracking"        element={<QRCodeProductionTracking />} />
              <Route path="/manufacturer-production-dashboard"  element={<ManufacturerProductionDashboard />} />
            </Route>

            <Route element={<ProtectedRoute allowedRoles={ROUTE_ACCESS.workshop.allowedRoles} allowedPermissions={ROUTE_ACCESS.workshop.allowedPermissions} />}>
              <Route path="/atelier-lot" element={<WorkshopLotPage />} />
            </Route>

            <Route element={<ProtectedRoute allowedRoles={ROUTE_ACCESS.productionSheet.allowedRoles} allowedPermissions={ROUTE_ACCESS.productionSheet.allowedPermissions} />}>
              <Route path="/production-sheet" element={<ProductionSheetPage />} />
            </Route>

            {/* ── CLIENT / DELIVERY  (admin can also access) ─────────── */}
            <Route element={<ProtectedRoute allowedRoles={ROUTE_ACCESS.clientDelivery.allowedRoles} allowedPermissions={ROUTE_ACCESS.clientDelivery.allowedPermissions} />}>
              <Route path="/client-delivery-tracking-portal" element={<ClientDeliveryTrackingPortal />} />
            </Route>

            {/* ── catch-all ────────────────────────────────────────────── */}
            <Route path="*" element={<NotFound />} />

          </RouterRoutes>
          </SidebarProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </BrowserRouter>
);

export default Routes;